import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api.ts';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext.tsx';
import { maskCurrency } from '../utils/balanceMask.ts';
import ItemizationPanel from '../components/ItemizationPanel.tsx';
import './Transactions.css';

interface Transaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  due_date: string;
  paid_date?: string;
  is_paid: boolean;
  account_id: number;
  destination_account_id?: number;
  category_id: number;
  account: {
    id: number;
    name: string;
  };
  destination_account?: {
    id: number;
    name: string;
  };
  category: {
    id: number;
    name: string;
    icon: string;
    icon_color: string;
    is_itemizable?: boolean;
  };
  recurring_transaction_id?: number;
  is_recurring?: boolean;
  installment_number?: number;
  created_at: string;
  updated_at?: string;
}

interface Account {
  id: number;
  name: string;
  is_default?: boolean;
  is_investment?: boolean;
}

interface Category {
  id: number;
  name: string;
  type: 'income' | 'expense';
  icon: string;
  icon_color: string;
  is_default?: boolean;
  is_itemizable?: boolean;
}

const Transactions: React.FC = () => {
  const { isVisible } = useBalanceVisibility();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [competenceFilter, setCompetenceFilter] = useState<string>('');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [formData, setFormData] = useState({
    description: '',
    amount: 0,
    type: 'expense' as 'income' | 'expense' | 'transfer',
    due_date: new Date().toISOString().split('T')[0],
    paid_date: '',
    account_id: 0,
    destination_account_id: 0,
    category_id: 0,
    is_recurring: false,
    recurrence_type: 'fixed_monthly' as 'fixed_monthly' | 'fixed_yearly' | 'installment',
    end_date: '',
    installments: undefined as number | undefined
  });
  const [amountInput, setAmountInput] = useState('');
  const [editScope, setEditScope] = useState<'this' | 'future' | null>(null);
  const [showEditScopeModal, setShowEditScopeModal] = useState(false);
  const [deleteScope, setDeleteScope] = useState<'all' | 'future' | null>(null);
  const [showDeleteScopeModal, setShowDeleteScopeModal] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState<Transaction | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editScopeSaving, setEditScopeSaving] = useState(false);
  const [deleteScopeSaving, setDeleteScopeSaving] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [itemizationTransaction, setItemizationTransaction] = useState<Transaction | null>(null);
  const manuallyExpandedGroupsRef = useRef<Set<string>>(new Set());

  // Função para enriquecer transações com dados de category e account
  const enrichTransactions = useCallback((transactionsData: any[], categoriesList: Category[], accountsList: Account[]): Transaction[] => {
    return transactionsData.map((t: any) => {
      // Se já tem category e account populados (vindo do backend), retorna como está
      if (t.category && t.account) {
        return t as Transaction;
      }
      
      // Caso contrário, busca nos arrays locais de categories e accounts
      const category = categoriesList.find(c => 
        c.id === t.category_id || 
        (c as any).server_id === t.category_id ||
        t.category_id === (c as any).server_id
      );
      const account = accountsList.find(a => 
        a.id === t.account_id || 
        (a as any).server_id === t.account_id ||
        t.account_id === (a as any).server_id
      );
      const destinationAccount = accountsList.find(a =>
        a.id === t.destination_account_id ||
        (a as any).server_id === t.destination_account_id ||
        t.destination_account_id === (a as any).server_id
      );
      
      return {
        ...t,
        category: category ? {
          id: category.id,
          name: category.name,
          icon: category.icon || '📋',
          icon_color: category.icon_color || '#666666'
        } : {
          id: t.category_id,
          name: 'Categoria não encontrada',
          icon: '📋',
          icon_color: '#666666'
        },
        account: account ? {
          id: account.id,
          name: account.name
        } : {
          id: t.account_id,
          name: 'Conta não encontrada'
        },
        destination_account: destinationAccount ? {
          id: destinationAccount.id,
          name: destinationAccount.name
        } : (t.destination_account_id ? {
          id: t.destination_account_id,
          name: 'Conta destino não encontrada'
        } : undefined)
      } as Transaction;
    });
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/transactions', {
        params: {
          competence: competenceFilter
        }
      });
      const transactionsData = response.data || [];
      // Enriquecer transações com dados de category e account
      const enriched = enrichTransactions(transactionsData, categories, accounts);
      setTransactions(enriched);
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
      // Define array vazio para evitar tela em branco
      setTransactions([]);
    }
  }, [competenceFilter, enrichTransactions, categories, accounts]);

  useEffect(() => {
    // Inicializar com o mês atual
    const today = new Date();
    setCurrentDate(today);
    updateCompetenceFilter(today);
    
    // Carregar dados iniciais
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Buscar transações quando o filtro mudar
  useEffect(() => {
    if (competenceFilter) {
      fetchTransactions();
    }
  }, [competenceFilter, fetchTransactions]);

  // Re-enriquecer transações quando categories ou accounts mudarem
  useEffect(() => {
    if (transactions.length > 0 && (categories.length > 0 || accounts.length > 0)) {
      // Verificar se alguma transação precisa ser enriquecida
      const needsEnrichment = transactions.some(t => !t.category || !t.account);
      if (needsEnrichment) {
        const enriched = enrichTransactions(transactions, categories, accounts);
        setTransactions(enriched);
      }
    }
  }, [categories, accounts, enrichTransactions, transactions]);

  // Scroll automático quando o formulário for aberto
  useEffect(() => {
    if (showForm && formRef.current) {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        formRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        // Fallback: scroll para o topo da página se o ref não funcionar
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [showForm]);

  const loadInitialData = async () => {
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        api.get('/api/v1/accounts'),
        api.get('/api/v1/categories')
      ]);

      const accountsData = accountsRes.data || [];
      const categoriesData = categoriesRes.data || [];
      
      setAccounts(accountsData);
      setCategories(categoriesData);

      // Pré-selecionar conta padrão se existir
      const defaultAccount = accountsData.find((acc: Account) => acc.is_default);
      if (defaultAccount && formData.account_id === 0) {
        setFormData(prev => ({ ...prev, account_id: defaultAccount.id }));
      }

      // Pré-selecionar categoria padrão do tipo selecionado se existir
      const defaultCategory = categoriesData.find((cat: Category) => 
        cat.is_default && cat.type === formData.type
      );
      if (defaultCategory && formData.category_id === 0) {
        setFormData(prev => ({ ...prev, category_id: defaultCategory.id }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      // Define arrays vazios para evitar tela em branco
      setAccounts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Preparar payload com dados de pagamento
      const payload: any = {
        ...formData,
        paid_date: isPaid ? formData.paid_date : '',
        installments: formData.installments || undefined
      };
      // Remover campos vazios
      if (!payload.end_date) delete payload.end_date;
      // Para parceladas, não usamos data final (apenas número de parcelas)
      if (payload.recurrence_type === 'installment') delete payload.end_date;
      if (!payload.installments) delete payload.installments;
      if (!isPaid) delete payload.paid_date;
      if (payload.type !== 'transfer') delete payload.destination_account_id;
      if (payload.type === 'transfer' && (!payload.destination_account_id || payload.destination_account_id === 0)) {
        console.error('Conta destino é obrigatória para transferência');
        return;
      }

      if (editingTransaction) {
        // Se for transação recorrente existente, mostrar modal para escolher escopo
        if (editingTransaction.is_recurring && !formData.is_recurring) {
          // Removendo recorrência - apenas editar transação
          const updatePayload = {
            description: formData.description,
            amount: formData.amount,
            type: formData.type,
            account_id: formData.account_id,
            destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
            category_id: formData.category_id,
            due_date: formData.due_date,
            paid_date: isPaid ? formData.paid_date : ''
          };
          await api.put(`/api/v1/transactions/${editingTransaction.id}`, updatePayload);
        } else if (editingTransaction.is_recurring && formData.is_recurring) {
          // Mantendo como recorrente - mostrar modal para escolher escopo
          setShowEditScopeModal(true);
          return;
        } else if (!editingTransaction.is_recurring && formData.is_recurring) {
          // Tornando transação não recorrente em recorrente - criar recorrência
          const recurringPayload: any = {
            description: formData.description,
            amount: formData.amount,
            type: formData.type,
            account_id: formData.account_id,
            destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
            category_id: formData.category_id,
            recurrence_type: formData.recurrence_type,
            start_date: formData.due_date,
            end_date: formData.recurrence_type === 'installment' ? undefined : (formData.end_date || undefined),
            installments: formData.installments || undefined
          };
          await api.post('/api/v1/recurring-transactions', recurringPayload);
          // Deletar a transação original e usar a gerada pela recorrência
          await api.delete(`/api/v1/transactions/${editingTransaction.id}`);
        } else {
          // Transação normal - apenas editar
          const updatePayload = {
            description: formData.description,
            amount: formData.amount,
            type: formData.type,
            account_id: formData.account_id,
            destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
            category_id: formData.category_id,
            due_date: formData.due_date,
            paid_date: isPaid ? formData.paid_date : ''
          };
          await api.put(`/api/v1/transactions/${editingTransaction.id}`, updatePayload);
        }
      } else {
        await api.post('/api/v1/transactions', payload);
      }
      resetForm();
      fetchTransactions();
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    // Buscar padrões antes de resetar
    const defaultAccount = accounts.find(acc => acc.is_default);
    const defaultCategory = categories.find(cat => cat.is_default && cat.type === 'expense');
    
    setFormData({
      description: '',
      amount: 0,
      type: 'expense',
      due_date: new Date().toISOString().split('T')[0],
      paid_date: '',
      account_id: defaultAccount ? defaultAccount.id : 0,
      destination_account_id: 0,
      category_id: defaultCategory ? defaultCategory.id : 0,
      is_recurring: false,
      recurrence_type: 'fixed_monthly',
      end_date: '',
      installments: undefined
    });
    setAmountInput('');
    setIsPaid(false);
    setShowForm(false);
    setEditingTransaction(null);
    setEditScope(null);
    setShowEditScopeModal(false);
  };

  const handleEditScopeConfirm = async () => {
    if (!editScope || !editingTransaction) return;
    
    setEditScopeSaving(true);
    try {
      if (editScope === 'this') {
        // Editar apenas esta transação
        const payload = {
          description: formData.description,
          amount: formData.amount,
          type: formData.type,
          account_id: formData.account_id,
          destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
          category_id: formData.category_id,
          due_date: formData.due_date,
          paid_date: isPaid ? formData.paid_date : ''
        };
        await api.put(`/api/v1/transactions/${editingTransaction.id}`, payload);
      } else {
        // Editar todas as futuras - atualizar recorrência e transações já geradas
        if (editingTransaction.recurring_transaction_id) {
          // Normalizar a data para garantir formato YYYY-MM-DD
          const normalizedDueDate = normalizeDate(formData.due_date);
          
          // Atualizar a recorrência
          await api.put(`/api/v1/recurring-transactions/${editingTransaction.recurring_transaction_id}`, {
            description: formData.description,
            amount: formData.amount,
            account_id: formData.account_id,
            destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
            category_id: formData.category_id,
            recurrence_type: 'fixed_monthly', // Manter tipo original por enquanto
            start_date: normalizedDueDate,
            end_date: formData.end_date ? normalizeDate(formData.end_date) : undefined
          });
          
          // Atualizar a transação atual
          await api.put(`/api/v1/transactions/${editingTransaction.id}`, {
            description: formData.description,
            amount: formData.amount,
            account_id: formData.account_id,
            destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
            category_id: formData.category_id,
            due_date: formData.due_date,
            type: formData.type
          });
          
          // Buscar e atualizar todas as transações futuras já geradas desta recorrência
          try {
            // Usar endpoint específico para buscar transações por recorrência (mais eficiente)
            const recurringTransactionsResponse = await api.get(`/api/v1/transactions/by-recurring/${editingTransaction.recurring_transaction_id}`);
            const allRecurringTransactions = recurringTransactionsResponse.data || [];
            
            const currentDueDate = normalizeDate(editingTransaction.due_date);
            const currentDateObj = new Date(currentDueDate);
            currentDateObj.setHours(0, 0, 0, 0);
            
            const futureTransactions = allRecurringTransactions.filter((t: Transaction) => {
              // Filtrar transações da mesma recorrência
              if (!t.recurring_transaction_id || t.recurring_transaction_id !== editingTransaction.recurring_transaction_id) {
                return false;
              }
              
              // Excluir a transação atual pelo ID (não pela data, pois pode haver múltiplas no mesmo dia)
              if (t.id === editingTransaction.id) {
                return false;
              }
              
              // Apenas transações futuras (maior ou igual à data atual, mas excluindo a atual pelo ID acima)
              const transactionDueDate = normalizeDate(t.due_date);
              const transactionDateObj = new Date(transactionDueDate);
              transactionDateObj.setHours(0, 0, 0, 0);
              
              // Comparar datas: transação deve ser futura ou igual (mas já excluímos a atual pelo ID)
              return transactionDateObj >= currentDateObj;
            });
            
            console.log(`Encontradas ${futureTransactions.length} transações futuras para atualizar`);
            
            // Atualizar cada transação futura
            for (const futureTransaction of futureTransactions) {
              // Calcular nova data de vencimento baseada na diferença de meses
              const currentDueDate = normalizeDate(editingTransaction.due_date);
              const futureDueDate = normalizeDate(futureTransaction.due_date);
              const currentDateObj = new Date(currentDueDate);
              const futureDateObj = new Date(futureDueDate);
              
              // Calcular diferença em meses
              const monthsDiff = (futureDateObj.getFullYear() - currentDateObj.getFullYear()) * 12 + 
                                (futureDateObj.getMonth() - currentDateObj.getMonth());
              
              // Calcular nova data baseada na data editada + diferença de meses
              const newDueDateObj = new Date(formData.due_date);
              newDueDateObj.setMonth(newDueDateObj.getMonth() + monthsDiff);
              
              // Ajustar se o dia não existe no mês (ex: 31 em fevereiro)
              const originalDay = newDueDateObj.getDate();
              const lastDayOfMonth = new Date(newDueDateObj.getFullYear(), newDueDateObj.getMonth() + 1, 0).getDate();
              if (originalDay > lastDayOfMonth) {
                newDueDateObj.setDate(lastDayOfMonth);
              }
              
              const newDueDate = newDueDateObj.toISOString().split('T')[0];
              
              try {
                await api.put(`/api/v1/transactions/${futureTransaction.id}`, {
                  description: formData.description,
                  amount: formData.amount,
                  account_id: formData.account_id,
                  destination_account_id: formData.type === 'transfer' ? formData.destination_account_id : undefined,
                  category_id: formData.category_id,
                  due_date: newDueDate,
                  type: formData.type
                });
                console.log(`Transação ${futureTransaction.id} atualizada com sucesso`);
              } catch (updateError) {
                console.error(`Erro ao atualizar transação ${futureTransaction.id}:`, updateError);
                // Continuar com as próximas transações mesmo se houver erro
              }
            }
            
            console.log(`Atualização de transações futuras concluída`);
          } catch (error) {
            console.error('Erro ao buscar transações futuras:', error);
            // Continuar mesmo se houver erro ao buscar futuras
          }
        }
      }
      resetForm();
      fetchTransactions();
    } catch (error) {
      console.error('Erro ao editar transação recorrente:', error);
    } finally {
      setEditScopeSaving(false);
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    const amount = Math.abs(transaction.amount);
    const paid = transaction.is_paid || false;
    setIsPaid(paid);
    setFormData({
      description: transaction.description,
      amount: amount,
      type: transaction.type,
      due_date: normalizeDate(transaction.due_date),
      paid_date: transaction.paid_date ? normalizeDate(transaction.paid_date) : (paid ? new Date().toISOString().split('T')[0] : ''),
      account_id: transaction.account_id,
      destination_account_id: transaction.destination_account_id || 0,
      category_id: transaction.category_id,
      is_recurring: transaction.is_recurring || false,
      recurrence_type: 'fixed_monthly',
      end_date: '',
      installments: undefined
    });
    setAmountInput(amount > 0 ? amount.toString().replace('.', ',') : '');
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTransaction(null);
    setIsPaid(false);
    setFormData({
      description: '',
      amount: 0,
      type: 'expense',
      due_date: new Date().toISOString().split('T')[0],
      paid_date: '',
      account_id: 0,
      destination_account_id: 0,
      category_id: 0,
      is_recurring: false,
      recurrence_type: 'fixed_monthly',
      end_date: '',
      installments: undefined
    });
    setAmountInput('');
  };

  const handleMarkAsPaid = async (id: number) => {
    setTogglingId(id);
    try {
      await api.put(`/api/v1/transactions/${id}/mark-paid`);
      fetchTransactions();
    } catch (error) {
      console.error('Erro ao marcar transação como paga:', error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleMarkAsUnpaid = async (id: number) => {
    setTogglingId(id);
    try {
      await api.put(`/api/v1/transactions/${id}/mark-unpaid`);
      fetchTransactions();
    } catch (error) {
      console.error('Erro ao desfazer transação paga:', error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    const tx = transactions.find(t => t.id === id);
    if (!tx) return;

    // Se for transação recorrente, perguntar escopo de exclusão
    if (tx.is_recurring && tx.recurring_transaction_id) {
      setDeletingTransaction(tx);
      setDeleteScope('future');
      setShowDeleteScopeModal(true);
      return;
    }

    if (window.confirm('Tem certeza que deseja excluir esta transação?')) {
      setDeletingId(id);
      try {
        await api.delete(`/api/v1/transactions/${id}`);
        fetchTransactions();
      } catch (error) {
        console.error('Erro ao excluir transação:', error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleDeleteScopeConfirm = async () => {
    if (!deleteScope || !deletingTransaction?.recurring_transaction_id) return;
    setDeleteScopeSaving(true);
    try {
      await api.post(`/api/v1/recurring-transactions/${deletingTransaction.recurring_transaction_id}/delete-scope`, {
        scope: deleteScope,
        from_due_date: deleteScope === 'future' ? normalizeDate(deletingTransaction.due_date) : undefined
      });
      setShowDeleteScopeModal(false);
      setDeleteScope(null);
      setDeletingTransaction(null);
      fetchTransactions();
    } catch (error) {
      console.error('Erro ao excluir recorrência:', error);
    } finally {
      setDeleteScopeSaving(false);
    }
  };

  const filteredCategories = formData.type === 'transfer'
    ? categories
    : categories.filter(cat => cat.type === formData.type);

  // Gerar opções de competência
  const updateCompetenceFilter = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    setCompetenceFilter(`${month}/${year}`);
  };

  const navigateToPreviousMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    setCurrentDate(newDate);
    updateCompetenceFilter(newDate);
  };

  const navigateToNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    setCurrentDate(newDate);
    updateCompetenceFilter(newDate);
  };

  const navigateToCurrentMonth = () => {
    const today = new Date();
    setCurrentDate(today);
    updateCompetenceFilter(today);
  };

  const getCurrentMonthLabel = () => {
    return currentDate.toLocaleDateString('pt-BR', { 
      month: 'long', 
      year: 'numeric' 
    }).charAt(0).toUpperCase() + currentDate.toLocaleDateString('pt-BR', { 
      month: 'long', 
      year: 'numeric' 
    }).slice(1);
  };

  // Função para agrupar transações por data de vencimento
  const groupTransactionsByDueDate = useCallback((transactions: Transaction[]): Map<string, Transaction[]> => {
    const grouped = new Map<string, Transaction[]>();
    
    transactions.forEach(transaction => {
      const normalizedDate = normalizeDate(transaction.due_date);
      const dateKey = normalizedDate; // Usar YYYY-MM-DD como chave
      
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      
      grouped.get(dateKey)!.push(transaction);
    });
    
    // Ordenar as chaves (datas) em ordem crescente
    const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });
    
    // Criar novo Map ordenado
    const sortedGrouped = new Map<string, Transaction[]>();
    sortedKeys.forEach(key => {
      sortedGrouped.set(key, grouped.get(key)!);
    });
    
    return sortedGrouped;
  }, []);

  // Função para verificar se todas as transações de um grupo estão pagas
  const areAllTransactionsPaid = (transactions: Transaction[]): boolean => {
    return transactions.length > 0 && transactions.every(t => t.is_paid);
  };

  // Função para alternar colapso de um grupo
  const toggleGroupCollapse = (dateKey: string) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        // Expandindo manualmente
        newSet.delete(dateKey);
        manuallyExpandedGroupsRef.current.add(dateKey);
      } else {
        // Colapsando manualmente
        newSet.add(dateKey);
        manuallyExpandedGroupsRef.current.delete(dateKey);
      }
      return newSet;
    });
  };

  // Efeito para colapsar automaticamente grupos com todas as transações pagas
  useEffect(() => {
    if (transactions.length === 0) {
      setCollapsedGroups(new Set());
      manuallyExpandedGroupsRef.current.clear();
      return;
    }
    
    const grouped = groupTransactionsByDueDate(transactions);
    
    setCollapsedGroups(prev => {
      const newCollapsedGroups = new Set<string>();
      
      // Para cada grupo, verificar se todas as transações estão pagas
      grouped.forEach((dateTransactions, dateKey) => {
        const allPaid = areAllTransactionsPaid(dateTransactions);
        const wasManuallyExpanded = manuallyExpandedGroupsRef.current.has(dateKey);
        
        if (allPaid && !wasManuallyExpanded) {
          // Se todas estão pagas e não foi expandido manualmente, colapsar
          newCollapsedGroups.add(dateKey);
        } else if (!allPaid) {
          // Se há transações não pagas, expandir automaticamente
          // Remover do Set de expandidos manualmente se estava lá
          manuallyExpandedGroupsRef.current.delete(dateKey);
          // Não adicionar ao Set de colapsados (mantém expandido)
        } else if (wasManuallyExpanded) {
          // Se foi expandido manualmente, respeitar a escolha do usuário
          // Não adicionar ao Set (mantém expandido)
        } else if (prev.has(dateKey) && allPaid) {
          // Se já estava colapsado e todas estão pagas, manter colapsado
          newCollapsedGroups.add(dateKey);
        }
      });
      
      return newCollapsedGroups;
    });
  }, [transactions, groupTransactionsByDueDate]);

  // Função para formatar data de vencimento como cabeçalho (ex: "10 de Novembro")
  const formatDueDateHeader = (dateString: string): string => {
    try {
      const normalizedDate = normalizeDate(dateString);
      const [year, month, day] = normalizedDate.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'long'
      });
    } catch (error) {
      return formatDate(dateString);
    }
  };

  const formatDate = (dateString: string, includeTime: boolean = false) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      
      if (includeTime) {
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Para datas sem hora, usar UTC para evitar problemas de fuso horário
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Se é formato YYYY-MM-DD, construir data local diretamente
        const [year, month, day] = dateString.split('-');
        const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return localDate.toLocaleDateString('pt-BR');
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const normalizeDate = (dateString: string) => {
    if (!dateString) return '';
    // Se contém timestamp, extrair apenas a data
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  };

  // Funções para formatação de valor monetário
  const formatAmountForDisplay = (value: number): string => {
    if (value === 0) return '';
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseAmountFromInput = (input: string): number => {
    if (!input) return 0;
    // Remove tudo exceto números, vírgulas e pontos
    const cleanInput = input.replace(/[^\d,]/g, '');
    // Converte vírgula para ponto para parseFloat
    const numericString = cleanInput.replace(',', '.');
    return parseFloat(numericString) || 0;
  };

  const handleAmountChange = (input: string) => {
    setAmountInput(input);
    const numericValue = parseAmountFromInput(input);
    setFormData({ ...formData, amount: numericValue });
  };

  const handleAmountBlur = () => {
    if (formData.amount > 0) {
      setAmountInput(formatAmountForDisplay(formData.amount));
    }
  };

  if (loading) {
    return (
      <div className="transactions">
        <div className="page-header">
          <h1>💰 Transações</h1>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions">
        <div className="page-header">
          <h1>💰 Transações</h1>
          <p>Registre suas receitas e despesas</p>
          <button
            className="primary-button"
            onClick={() => {
              // Buscar padrões antes de abrir formulário
              const defaultAccount = accounts.find(acc => acc.is_default);
              const defaultCategory = categories.find(cat => cat.is_default && cat.type === 'expense');
              
              setEditingTransaction(null);
              setFormData({
                description: '',
                amount: 0,
                type: 'expense',
                due_date: new Date().toISOString().split('T')[0],
                paid_date: '',
                account_id: defaultAccount ? defaultAccount.id : 0,
                category_id: defaultCategory ? defaultCategory.id : 0,
                is_recurring: false,
                recurrence_type: 'fixed_monthly',
                end_date: '',
                installments: undefined
              });
              setAmountInput('');
              setIsPaid(false);
              setShowForm(!showForm);
            }}
          >
            {showForm ? 'Cancelar' : 'Nova Transação'}
          </button>
        </div>

            {/* Navegação por competência */}
            <div className="filter-section">
              <div className="competence-navigation">
                <div className="navigation-controls">
                  <button
                    type="button"
                    className="nav-button prev-button"
                    onClick={navigateToPreviousMonth}
                    title="Mês anterior"
                  >
                    ←
                  </button>
                  
                  <div className="current-month">
                    <h3>{getCurrentMonthLabel()}</h3>
                    <button
                      type="button"
                      className="current-month-button"
                      onClick={navigateToCurrentMonth}
                      title="Voltar ao mês atual"
                    >
                      📅 Hoje
                    </button>
                  </div>
                  
                  <button
                    type="button"
                    className="nav-button next-button"
                    onClick={navigateToNextMonth}
                    title="Próximo mês"
                  >
                    →
                  </button>
                </div>
                
                <div className="filter-info">
                  <span>
                    {transactions.length} transação(ões) em {competenceFilter}
                  </span>
                </div>
              </div>
            </div>

      {showForm && (
        <div className="form-container" ref={formRef}>
          <form onSubmit={handleSubmit} className="transaction-form">
            <h3>{editingTransaction ? 'Editar Transação' : 'Nova Transação'}</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="description">Descrição</label>
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Ex: Salário, Supermercado..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="amount">Valor</label>
                <input
                  type="text"
                  id="amount"
                  value={amountInput}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  onBlur={handleAmountBlur}
                  onFocus={() => {
                    // Ao focar, mostra o valor numérico para edição
                    if (formData.amount > 0) {
                      setAmountInput(formData.amount.toString().replace('.', ','));
                    }
                  }}
                  required
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="type">Tipo</label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value as 'income' | 'expense' | 'transfer';
                    // Buscar categoria padrão do novo tipo (para transfer, manter a atual se existir)
                    const defaultCategory = newType === 'transfer'
                      ? undefined
                      : categories.find(cat => cat.is_default && cat.type === newType);
                    setFormData({ 
                      ...formData, 
                      type: newType, 
                      category_id: defaultCategory ? defaultCategory.id : (newType === 'transfer' ? formData.category_id : 0),
                      destination_account_id: newType === 'transfer' ? (formData.destination_account_id || 0) : 0
                    });
                  }}
                >
                  <option value="expense">💸 Despesa</option>
                  <option value="income">💰 Receita</option>
                  <option value="transfer">⇄ Transferência</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="date">Vencimento</label>
                <input
                  type="date"
                  id="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="account_id">Conta</label>
                <select
                  id="account_id"
                  value={formData.account_id}
                  onChange={(e) => setFormData({ ...formData, account_id: parseInt(e.target.value) })}
                  required
                >
                  <option value={0}>Selecione uma conta</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
              {formData.type === 'transfer' ? (
                <div className="form-group">
                  <label htmlFor="destination_account_id">Conta destino</label>
                  <select
                    id="destination_account_id"
                    value={formData.destination_account_id}
                    onChange={(e) => setFormData({ ...formData, destination_account_id: parseInt(e.target.value) })}
                    required
                  >
                    <option value={0}>Selecione a conta destino</option>
                    {accounts
                      .filter(a => a.id !== formData.account_id)
                      .map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="category_id">Categoria</label>
                  <select
                    id="category_id"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                    required
                  >
                    <option value={0}>Selecione uma categoria</option>
                    {filteredCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Categoria para transferência */}
            {formData.type === 'transfer' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category_id">Categoria</label>
                  <select
                    id="category_id"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                    required
                  >
                    <option value={0}>Selecione uma categoria</option>
                    {filteredCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  {/* vazio para manter layout */}
                </div>
              </div>
            )}

            {/* Campos de recorrência - permitir tornar recorrente ao editar também */}
            <div className="form-row">
              <div className="form-group">
                <div className="toggle-container">
                  <label htmlFor="is_recurring" className="toggle-label">
                    Tornar recorrente
                  </label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      id="is_recurring"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {formData.is_recurring && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="recurrence_type">Tipo de Recorrência</label>
                    <select
                      id="recurrence_type"
                      value={formData.recurrence_type}
                      onChange={(e) => {
                        const next = e.target.value as 'fixed_monthly' | 'fixed_yearly' | 'installment';
                        setFormData({
                          ...formData,
                          recurrence_type: next,
                          // Parceladas não usam data final
                          end_date: next === 'installment' ? '' : formData.end_date
                        });
                      }}
                    >
                      <option value="fixed_monthly">Fixa Mensal</option>
                      <option value="fixed_yearly">Fixa Anual</option>
                      <option value="installment">Parcelada</option>
                    </select>
                  </div>
                  {formData.recurrence_type !== 'installment' && (
                    <div className="form-group">
                      <label htmlFor="end_date">Data de Término (Opcional)</label>
                      <input
                        type="date"
                        id="end_date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      />
                    </div>
                  )}
                </div>
                {formData.recurrence_type === 'installment' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="installments">Número de Parcelas</label>
                      <input
                        type="number"
                        id="installments"
                        min="1"
                        value={formData.installments || ''}
                        onChange={(e) => setFormData({ ...formData, installments: e.target.value ? parseInt(e.target.value) : undefined })}
                        required
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Informações de criação e atualização - apenas no modo de edição */}
            {editingTransaction && (
              <div className="transaction-metadata">
                <div className="metadata-item">
                  <span className="metadata-label">Criado em:</span>
                  <span className="metadata-value">{formatDate(editingTransaction.created_at, true)}</span>
                </div>
                {editingTransaction.updated_at && editingTransaction.updated_at !== editingTransaction.created_at && (
                  <div className="metadata-item">
                    <span className="metadata-label">Última atualização:</span>
                    <span className="metadata-value">{formatDate(editingTransaction.updated_at, true)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Toggle e campo de pagamento no final do formulário */}
            <div className="form-row">
              <div className="form-group">
                <div className="toggle-container">
                  <label htmlFor="is_paid" className="toggle-label">
                    {formData.type === 'income'
                      ? 'Já foi recebido?'
                      : (formData.type === 'transfer' ? 'Já foi transferido?' : 'Já foi pago?')}
                  </label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      id="is_paid"
                      checked={isPaid}
                      onChange={(e) => {
                        const paid = e.target.checked;
                        setIsPaid(paid);
                        if (paid) {
                          // Se marcou como pago, definir data atual como default
                          const today = new Date().toISOString().split('T')[0];
                          setFormData({ ...formData, paid_date: today });
                        } else {
                          // Se desmarcou, limpar data de pagamento
                          setFormData({ ...formData, paid_date: '' });
                        }
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {isPaid && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="paid_date">
                    {formData.type === 'income'
                      ? 'Data de Recebimento'
                      : (formData.type === 'transfer' ? 'Data da Transferência' : 'Data de Pagamento')}
                  </label>
                  <input
                    type="date"
                    id="paid_date"
                    value={formData.paid_date}
                    onChange={(e) => setFormData({ ...formData, paid_date: e.target.value })}
                    required={isPaid}
                  />
                </div>
                <div className="form-group">
                  {/* Campo vazio para manter layout */}
                </div>
              </div>
            )}
            
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingTransaction ? 'Salvar Alterações' : 'Criar Transação')}
              </button>
              <button 
                type="button" 
                className="secondary-button"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

        <div className="transactions-content">
          {transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💰</div>
            <h3>
              {transactions.length === 0 
                ? 'Nenhuma transação registrada' 
                : `Nenhuma transação em ${competenceFilter}`
              }
            </h3>
            <p>
              {transactions.length === 0 
                ? 'Comece registrando suas receitas e despesas' 
                : 'Tente selecionar outro período ou adicione uma nova transação'
              }
            </p>
            <button 
              className="primary-button"
              onClick={() => {
                resetForm();
                setFormData({
                  ...formData,
                  due_date: new Date().toISOString().split('T')[0]
                });
                setShowForm(true);
              }}
            >
              Adicionar Transação
            </button>
          </div>
        ) : (
            <div className="transactions-list">
              {Array.from(groupTransactionsByDueDate(transactions).entries()).map(([dateKey, dateTransactions]) => {
                const isCollapsed = collapsedGroups.has(dateKey);
                const allPaid = areAllTransactionsPaid(dateTransactions);
                
                return (
                <div key={dateKey} className="transaction-date-group">
                  <div 
                    className="date-group-header"
                    onClick={() => toggleGroupCollapse(dateKey)}
                    style={{ cursor: 'pointer' }}
                  >
                    <h2 className="date-header-text">
                      {formatDueDateHeader(dateKey)}
                      {allPaid && (
                        <span className="all-paid-indicator" title="Todas as transações estão pagas/recebidas">
                          ✓
                        </span>
                      )}
                    </h2>
                    <button 
                      className={`collapse-button ${isCollapsed ? 'collapsed' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroupCollapse(dateKey);
                      }}
                      title={isCollapsed ? 'Expandir' : 'Colapsar'}
                      aria-label={isCollapsed ? 'Expandir' : 'Colapsar'}
                    >
                      {isCollapsed ? '▶' : '▼'}
                    </button>
                  </div>
                  <div className={`date-group-transactions ${isCollapsed ? 'collapsed' : ''}`}>
                    {dateTransactions.map((transaction) => {
                      // Proteção contra dados undefined
                      const category = transaction.category || { name: 'Categoria não encontrada', icon: '📋', icon_color: '#666666' };
                      const account = transaction.account || { name: 'Conta não encontrada' };
                      
                      // Determinar se a transação está vencida (apenas para transações não pagas)
                      let urgencyClass = '';
                      if (!transaction.is_paid) {
                        const dueDate = new Date(normalizeDate(transaction.due_date));
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        dueDate.setHours(0, 0, 0, 0);
                        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        if (daysUntilDue < 0) {
                          urgencyClass = 'overdue';
                        }
                      }
                      
                      return (
                        <div key={transaction.id} className={`transaction-card ${urgencyClass}`}>
                          <div className="transaction-header">
                            <div className="transaction-info">
                              <div 
                                className="category-icon" 
                                style={{ color: category.icon_color || '#666666' }}
                              >
                                {category.icon || '📋'}
                              </div>
                              <div>
                                <h3 className={transaction.is_paid ? 'paid-transaction' : ''}>
                                  {transaction.description}
                                  {transaction.is_recurring && (
                                    <span className="recurring-icon" title="Transação recorrente">🔄</span>
                                  )}
                                  {transaction.installment_number && (
                                    <span className="installment-badge" title={`Parcela ${transaction.installment_number}`}>
                                      {transaction.installment_number}
                                    </span>
                                  )}
                                </h3>
                                <p className="transaction-meta">
                                  {category.name} • {transaction.type === 'transfer'
                                    ? `${account.name} → ${(transaction.destination_account?.name || 'Conta destino')}`
                                    : account.name}
                                </p>
                                {transaction.is_paid && (
                                  <span className="paid-badge">
                                    💰 {transaction.type === 'income' ? 'Recebido' : (transaction.type === 'transfer' ? 'Transferido' : 'Pago')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="transaction-amount">
                              <span className={`amount ${transaction.type === 'transfer' ? 'transfer' : transaction.type}`}>
                                {transaction.type === 'transfer' ? '⇄' : (transaction.type === 'income' ? '+' : '-')}
                                {isVisible ? formatCurrency(Math.abs(transaction.amount)) : maskCurrency(formatCurrency(Math.abs(transaction.amount)))}
                              </span>
                              <div className="transaction-actions">
                                {!transaction.is_paid ? (
                                  <button
                                    className="mark-paid-button"
                                    onClick={() => handleMarkAsPaid(transaction.id)}
                                    title={transaction.type === 'income' ? 'Marcar como recebido' : (transaction.type === 'transfer' ? 'Marcar como transferido' : 'Marcar como pago')}
                                    disabled={togglingId === transaction.id}
                                  >
                                    {togglingId === transaction.id ? '...' : '💵'}
                                  </button>
                                ) : (
                                  <button
                                    className="mark-unpaid-button"
                                    onClick={() => handleMarkAsUnpaid(transaction.id)}
                                    title={transaction.type === 'income' ? 'Desfazer recebimento' : (transaction.type === 'transfer' ? 'Desfazer transferência' : 'Desfazer pagamento')}
                                    disabled={togglingId === transaction.id}
                                  >
                                    {togglingId === transaction.id ? '...' : '↩️'}
                                  </button>
                                )}
                                {category.is_itemizable && (
                                  <button
                                    className="edit-button"
                                    onClick={() => setItemizationTransaction(transaction)}
                                    title="Ver itens da transação"
                                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  >
                                    📋 Itens
                                  </button>
                                )}
                                <button
                                  className="edit-button"
                                  onClick={() => handleEdit(transaction)}
                                  title="Editar transação"
                                >
                                  <img src="/edit.svg" alt="Editar" className="icon" />
                                </button>
                                <button 
                                  className="delete-button"
                                  onClick={() => handleDelete(transaction.id)}
                                  title="Excluir transação"
                                  disabled={deletingId === transaction.id}
                                >
                                  {deletingId === transaction.id ? '...' : <img src="/delete.svg" alt="Excluir" className="icon" />}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="transaction-footer">
                            {transaction.paid_date && (
                              <span className="transaction-paid-date">
                                <strong>{
                                  transaction.type === 'income'
                                    ? 'Recebido em:'
                                    : (transaction.type === 'transfer' ? 'Transferido em:' : 'Pago em:')
                                }</strong> {formatDate(normalizeDate(transaction.paid_date))}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
              })}
          </div>
        )}
      </div>

      {/* Modal de escolha de escopo de edição para transações recorrentes */}
      {showEditScopeModal && (
        <div className="modal-overlay" onClick={() => setShowEditScopeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Editar Transação Recorrente</h3>
            <p>Como deseja editar esta transação?</p>
            <div className="edit-scope-options">
              <button
                className={`scope-button ${editScope === 'this' ? 'active' : ''}`}
                onClick={() => setEditScope('this')}
              >
                <strong>Editar apenas esta transação</strong>
                <small>As alterações não afetarão outras instâncias</small>
              </button>
              <button
                className={`scope-button ${editScope === 'future' ? 'active' : ''}`}
                onClick={() => setEditScope('future')}
              >
                <strong>Editar todas as transações futuras</strong>
                <small>Atualizará a recorrência e regenerará transações futuras</small>
              </button>
            </div>
            <div className="modal-actions">
              <button
                className="primary-button"
                onClick={handleEditScopeConfirm}
                disabled={!editScope || editScopeSaving}
              >
                {editScopeSaving ? 'Processando...' : 'Confirmar'}
              </button>
              <button
                className="secondary-button"
                onClick={() => {
                  setShowEditScopeModal(false);
                  setEditScope(null);
                }}
                disabled={editScopeSaving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de escolha de escopo de exclusão para transações recorrentes */}
      {showDeleteScopeModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteScopeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Excluir Transação Recorrente</h3>
            <p>Como deseja excluir?</p>
            <div className="edit-scope-options">
              <button
                className={`scope-button ${deleteScope === 'future' ? 'active' : ''}`}
                onClick={() => setDeleteScope('future')}
              >
                Excluir a partir desta (inclui esta)
              </button>
              <button
                className={`scope-button ${deleteScope === 'all' ? 'active' : ''}`}
                onClick={() => setDeleteScope('all')}
              >
                Excluir todas
              </button>
            </div>
            <div className="modal-actions">
              <button
                className="primary-button"
                onClick={handleDeleteScopeConfirm}
                disabled={!deleteScope || deleteScopeSaving}
              >
                {deleteScopeSaving ? 'Excluindo...' : 'Confirmar'}
              </button>
              <button
                className="secondary-button"
                onClick={() => {
                  setShowDeleteScopeModal(false);
                  setDeleteScope(null);
                  setDeletingTransaction(null);
                }}
                disabled={deleteScopeSaving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Itemization modal */}
      {itemizationTransaction && (
        <div className="modal-overlay" onClick={() => setItemizationTransaction(null)}>
          <div className="modal-content" style={{ maxWidth: 680, width: '95vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>📋 Itens — {itemizationTransaction.description}</h3>
              <button
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280' }}
                onClick={() => setItemizationTransaction(null)}
              >
                ✕
              </button>
            </div>
            <ItemizationPanel
              transactionId={itemizationTransaction.id}
              transactionAmount={Math.abs(itemizationTransaction.amount)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;